/**
 * Row data: the job contracts (1.0b deliverable 13). The `job:` rows with their owners and gates
 * live in the worker, beside each job's module (`apps/worker/src/<module>/jobs.ts`): `codex.ping`
 * P0-2; `assets.variants` 1.R (`editor-alpha`); `derived.rebuild`, `derived.verify` and
 * `export.run` 1.P (`exit-1a`); `docs.rewrite-merged` 1.N-3 (`exit-1b`); `import.plan`,
 * `import.apply` and `import.undo` 2.B-1 (`exit-2a`; CS r4a). Pure data (excluded from mutation
 * testing).
 */
import { DeriverName } from '../derived/derivers.js';
import { Uuid } from '../dto/common.js';
import { EXAMPLE_IDS } from '../routes/example-ids.js';
import { z } from '../zod.js';
import { defineJob } from './define-job.js';
/** The formats an on-demand export writes. */
export const EXPORT_FORMATS = ['markdown', 'json'];
export const ExportFormat = z.enum(EXPORT_FORMATS);
export const JOBS = Object.freeze([
    defineJob({
        // The health.ping round trip; the server sends `{}` and the worker reads the job's own id.
        id: 'codex.ping',
        queue: 'codex.ping',
        payload: z.strictObject({ jobId: z.string().min(1).optional() }),
        example: {},
    }),
    defineJob({
        // The thumbnail and 1200-pixel variants of an uploaded image; `asset.ready` follows.
        id: 'assets.variants',
        queue: 'assets.variants',
        payload: z.strictObject({ assetId: Uuid }),
        example: { assetId: EXAMPLE_IDS.assetQuayEngraving.id },
    }),
    defineJob({
        // `pnpm db:rebuild <name>` and the startup rebuild of a deriver whose version changed.
        id: 'derived.rebuild',
        queue: 'derived.rebuild',
        payload: z.strictObject({ deriver: DeriverName }),
        example: { deriver: 'body' },
    }),
    defineJob({
        // Every deriver's `verify`, into the Health page and `derived_state.status`.
        id: 'derived.verify',
        queue: 'derived.verify',
        payload: z.strictObject({}),
        example: {},
    }),
    defineJob({
        // An on-demand export (decision 32); its id is the export's id (orchestrator ruling 8).
        id: 'export.run',
        queue: 'export.run',
        payload: z.strictObject({ exportId: Uuid, format: ExportFormat }),
        example: { exportId: EXAMPLE_IDS.exportMarkdown.id, format: 'markdown' },
    }),
    defineJob({
        // After a merge: rewrite the mentions of the merged document to its survivor.
        id: 'docs.rewrite-merged',
        queue: 'docs.rewrite-merged',
        payload: z.strictObject({ loserId: Uuid, survivorId: Uuid }),
        example: {
            loserId: EXAMPLE_IDS.noteLamplighterNotes.id,
            survivorId: EXAMPLE_IDS.questMissingLamplighters.id,
        },
    }),
    defineJob({
        // After `import.runs.complete`: read the export, build the plan, store it, propose matches.
        id: 'import.plan',
        queue: 'import.plan',
        payload: z.strictObject({ runId: Uuid }),
        example: { runId: EXAMPLE_IDS.importRunPlanned.id },
    }),
    defineJob({
        // After `import.runs.apply`: write the run's choices in one change_log batch.
        id: 'import.apply',
        queue: 'import.apply',
        payload: z.strictObject({ runId: Uuid }),
        example: { runId: EXAMPLE_IDS.importRunPlanned.id },
    }),
    defineJob({
        // After `import.runs.undo`: undo the newest applied run, page by page.
        id: 'import.undo',
        queue: 'import.undo',
        payload: z.strictObject({ runId: Uuid }),
        example: { runId: EXAMPLE_IDS.importRunApplied.id },
    }),
]);
//# sourceMappingURL=rows-jobs.js.map